<template>
  <div class="suggested-social-posts">
    <!-- Standard page header -->
    <div class="standard-page-header suggested-social-posts__header">
      <h1 class="global-h1 standard-page-header__title">Suggested Social Posts</h1>

      <div class="standard-page-header__subtitle-and-link">
        <div class="standard-page-header__subtitle">
          Shrpa suggests social posts based on your content.<br />
          Each week we'll generate new suggestions to keep your feed fresh!
        </div>

        <LinkWithIcon
          class="standard-page-header__link"
          icon-inset="1px auto auto -3px"
          tag="RouterLink"
          :to="{
            name: 'ShareStopSummaryV2',
            params: { pageId: route.params.pageId },
          }"
        >
          <template #icon><IconEmbedded name="checklist_2" :size="26" /></template>
          <span>Manual Creation</span>&nbsp;<em style="font-style: normal; opacity: 0.7">(5)</em>
        </LinkWithIcon>
      </div>
    </div>
    <!-- / Standard page header -->

    <!-- Generate from Photos -->
    <div style="margin-bottom: 36px; margin-top: -24px">
      <div class="standard-page-header__subtitle" style="margin-bottom: 5px">Want something different?</div>
      <SrpButton variant="primary" size="small" @click="openGenFromPhotosModal" :isDisabled="isGeneratingPosts">
        <template #icon><IconEmbedded name="plus_4" :size="20" /></template>
        Generate from Your Photos
      </SrpButton>

      <h1 v-if="manuallyCreatedPosts?.length > 0">Posts from Your Photos</h1>

      <div v-if="isGeneratingPosts" class="generating-posts-indicator">
        <Loader size="small" style="margin: 0" />
        <span>Generating posts...</span>
      </div>

      <ul class="suggested-social-posts__post-previews-list">
        <SocialPostPreview
          class="suggested-social-posts__post-preview"
          v-for="socialPost in manuallyCreatedPosts"
          :key="socialPost?.id"
          :assetIds="socialPost.assetIds"
          :caption="socialPost.caption || ''"
          :isWithTextarea="false"
          :isWithShareAndDislikeButtons="true"
          @clickCustomizeAndShare="
            async () => {
              postToEdit = lodashCloneDeep(socialPost);
              domRefEditAndShareSocialPostModal?.openModal();
              await loadShareSummaryPayloads();
            }
          "
          @clickDislike="() => console.log('clickDislike')"
          :fancyboxKey="socialPost?.id"
        />
      </ul>
    </div>
    <div :class="{ 'suggested-social-posts__modal-hidden': !isGenFromPhotosModalVisible }">
      <SocialPostGen
        ref="domRefSocialPostGen"
        :customerId="String(route.params.pageId)"
        @close="closeGenFromPhotosModal"
        @generatingStarted="handleGeneratingStarted"
        @generatingCompleted="handleGeneratingCompleted"
      />
    </div>
    <!-- / Generate from Photos -->

    <Loader v-if="isLoadingAllPosts" />

    <div v-else-if="autoGeneratedPosts.length === 0">
      <div class="standard-page-header__subtitle">
        <b style="font-size: 1.2em">Hold tight!</b><br />
        We'll generate some social posts for you soon.<br />
        Reach out to support@shrpa.com if you have questions in the meantime.
      </div>
    </div>

    <template v-else>
      <h1>Weekly Social Posts</h1>
      <ul class="suggested-social-posts__post-previews-list">
        <SocialPostPreview
          class="suggested-social-posts__post-preview"
          v-for="socialPost in autoGeneratedPosts"
          :key="socialPost?.id"
          :assetIds="socialPost.assetIds"
          :caption="socialPost.caption || ''"
          :isWithTextarea="false"
          :isWithShareAndDislikeButtons="true"
          @clickCustomizeAndShare="
            async () => {
              postToEdit = lodashCloneDeep(socialPost);
              domRefEditAndShareSocialPostModal?.openModal();
              await loadShareSummaryPayloads();
            }
          "
          @clickDislike="() => console.log('clickDislike')"
          :fancyboxKey="socialPost?.id"
        />
      </ul>
    </template>

    <EditAndShareSocialPostModal
      ref="domRefEditAndShareSocialPostModal"
      :postToEdit="postToEdit"
      :customerId="customerId"
      :isLoadingSummaryPayload="isLoadingSummaryPayload"
      :socialShareSummaryPayload="socialShareSummaryPayloads[postToEdit?.id]"
      @clickEditImages="
        async () => {
          await loadShareSummaryPayloads();
          domRefHighlightsSelectModal.openModal();
          domRefEditAndShareSocialPostModal.closeModal();
        }
      "
      @savePost="
        () => {
          const postIndex = allPosts.findIndex(p => p.id === postToEdit.id);
          allPosts[postIndex] = postToEdit;
        }
      "
    />

    <HighlightsSelectModal
      ref="domRefHighlightsSelectModal"
      :allAssetIds="socialShareSummaryPayloads[postToEdit?.id]?.assets || []"
      :selectedAssetIds="postToEdit?.assetIds"
      :saveCallBack="
        async selectedAssetIds => {
          postToEdit.assetIds = selectedAssetIds;
        }
      "
      @cancel="() => domRefEditAndShareSocialPostModal.openModal()"
      @done="() => domRefEditAndShareSocialPostModal.openModal()"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import lodashCloneDeep from "lodash-es/cloneDeep";

// Components
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import EditAndShareSocialPostModal from "./EditAndShareSocialPostModal.vue";
import SocialPostGen from "../SocialPostGen/index.vue";
import Loader from "@components/Loader/Loader.vue";
import HighlightsSelectModal from "@components/HighlightsSelectModal.vue";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";

// Types
import { SocialMediaPostData, SocialShareSummaryPayload } from "@contracts/shareStop";

// DOM refs
const domRefEditAndShareSocialPostModal = ref<any>(null);
const domRefHighlightsSelectModal = ref<any>(null);
const domRefSocialPostGen = ref<any>(null);
const isGenFromPhotosModalVisible = ref(false);
const isGeneratingPosts = ref(false);

// Router
const route = useRoute();

// Load posts =================================================================
const allPosts = ref<Array<SocialMediaPostData>>([]);
const autoGeneratedPosts = ref<Array<SocialMediaPostData>>([]);
const manuallyCreatedPosts = ref<Array<SocialMediaPostData>>([]);
const isLoadingAllPosts = ref<boolean>(true);

const customerId = ref<string>(String(route.params.pageId));

function groupData(posts: SocialMediaPostData[]) {
  // Split into groups (note: the systemCreated field can be removed once the data is refreshed)
  autoGeneratedPosts.value = posts.filter(post => post.autoGenerated || post.systemCreated).sort((a, b) => Date.parse(b.updatedDate) - Date.parse(a.updatedDate));
  manuallyCreatedPosts.value = posts.filter(post => !post.autoGenerated && post.createdWithAI).sort((a, b) => Date.parse(b.updatedDate) - Date.parse(a.updatedDate));
}

onMounted(async () => {
  isLoadingAllPosts.value = true;
  const posts = await ShareStopSummaryRepo.loadAllPosts(customerId.value);
  allPosts.value = posts;
  groupData(posts);
  isLoadingAllPosts.value = false;
});

// Post to edit ===============================================================
const postToEdit = ref<SocialMediaPostData | null>(null);

// Load SocialShareSummaryPayload =============================================
const isLoadingSummaryPayload = ref<boolean>(false);
const socialShareSummaryPayloads = ref<Record<string, SocialShareSummaryPayload>>({});

async function loadShareSummaryPayloads() {
  if (socialShareSummaryPayloads.value[postToEdit.value?.id]) {
    return;
  }

  isLoadingSummaryPayload.value = true;
  socialShareSummaryPayloads.value[postToEdit.value?.id] = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromPostId(customerId.value, postToEdit.value?.id);
  isLoadingSummaryPayload.value = false;
}

// Generate social posts =====================================================
function openGenFromPhotosModal() {
  isGenFromPhotosModalVisible.value = true;
  domRefSocialPostGen.value?.openModal();
}

function closeGenFromPhotosModal() {
  isGenFromPhotosModalVisible.value = false;
  domRefSocialPostGen.value?.closeModal();
}

function handleGeneratingStarted(postCount: number) {
  isGeneratingPosts.value = true;
}

function handleGeneratingCompleted(socialPosts: SocialMediaPostData[]) {
  isGeneratingPosts.value = false;
  // Update allPosts with the new posts
  allPosts.value = [...socialPosts, ...allPosts.value];
  // Regroup all posts
  groupData(allPosts.value);
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Suggested social posts =====================================================
.suggested-social-posts {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__header {
    margin-bottom: 45px;
  }

  &__post-previews-list {
    gap: $desktop-wide-grid-gap-width;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    list-style: none;
  }

  &__post-preview {
    width: $desktop-wide-right-col-width-1-3rd;
  }
}

.generating-posts-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  color: var(--color-text-secondary);
  font-size: 14px;
}
</style>
